import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Label, Input, Table, Button, Spinner } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";

/**
 * Requisicion de varios pedidos contra el inventario.
 *
 * Para los pedidos elegidos: lo que pide su explosion (con la merma de la orden
 * de maquila), lo que ya se mando a los maquileros, lo que falta mandar, lo que
 * hay en bodega y lo que falta comprar. El calculo vive en el back
 * (RequisicionService); aqui solo se muestra.
 */
function ReporteRequisicion() {
  const { user } = useContext(AuthContext);
  const URL_PEDIDOS = process.env.REACT_APP_URL_PEDIDOS;
  const URL_REQUISICION = process.env.REACT_APP_URL_REQUISICION;

  const [pedidos, setPedidos] = useState([]);
  const [elegidos, setElegidos] = useState([]);
  const [tipo, setTipo] = useState("Habilitacion");
  const [filas, setFilas] = useState([]);
  const [soloFaltante, setSoloFaltante] = useState(false);
  const [cargando, setCargando] = useState(false);

  const cabeceras = () => ({
    headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
  });

  useEffect(() => {
    axios
      .get(URL_PEDIDOS, cabeceras())
      .then((res) =>
        setPedidos(res.data.filter((p) => p.is_active !== "No").sort((a, b) => b.idPedido - a.idPedido))
      )
      .catch((err) => console.log(err));
  }, []);

  function generar() {
    if (!elegidos.length) {
      return Swal.fire({ icon: "warning", title: "Elige al menos un pedido" });
    }
    setCargando(true);
    const ids = elegidos.map((p) => p._id).join(",");
    const filtroTipo = tipo === "todos" ? "" : `&tipoMaterial=${tipo}`;
    axios
      .get(`${URL_REQUISICION}Pedidos?pedidos=${ids}${filtroTipo}`, cabeceras())
      .then((res) => {
        setFilas(
          res.data.renglones.sort((a, b) =>
            a.tipoMaterial + a.nombre + a.color > b.tipoMaterial + b.nombre + b.color ? 1 : -1
          )
        );
        if (!res.data.renglones.length) {
          Swal.fire({
            icon: "info",
            title: "Sin requisicion",
            text: "Los pedidos elegidos no tienen explosion capturada para ese tipo de material.",
          });
        }
      })
      .catch((err) =>
        Swal.fire({ icon: "error", title: "No se pudo generar", text: err.response?.data?.message || err.message })
      )
      .finally(() => setCargando(false));
  }

  const visibles = filas.filter((f) => !soloFaltante || f.faltante > 0);
  const n = (x) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(x || 0);
  const tipoTexto = (t) => (t === "Telas" ? "Tela" : "Habilitacion");
  const listaPedidos = elegidos.map((p) => p.idPedido).join(", ");

  const COLUMNAS = ["Tipo", "Material", "Descripcion", "Color", "Unidad", "Requerido", "Ya mandado", "Por mandar", "Existencia Bodega", "Faltante"];
  const renglones = () =>
    visibles.map((f) => [
      tipoTexto(f.tipoMaterial), f.nombre, f.descripcion, f.color, f.unidad,
      n(f.requerido), n(f.enviado), n(f.porEnviar), n(f.existencia), n(f.faltante),
    ]);

  function PDF() {
    const doc = new jsPDF("landscape");
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img, "png", 230, 8, 45, 22);
    doc.text("Requisicion de material", 20, 18);
    doc.setFontSize(10);
    doc.text(`Pedidos: ${listaPedidos}`, 20, 26);
    doc.autoTable({ head: [COLUMNAS], body: renglones(), startY: 34, styles: { fontSize: 8 } });
    doc.save("Requisicion.pdf");
  }

  function excel() {
    new ExportJsonExcel({
      fileName: "Requisicion",
      datas: [{
        sheetData: visibles.map((f) => ({
          Tipo: tipoTexto(f.tipoMaterial), Material: f.nombre, Descripcion: f.descripcion, Color: f.color,
          Unidad: f.unidad, Requerido: f.requerido, "Ya mandado": f.enviado, "Por mandar": f.porEnviar,
          "Existencia Bodega": f.existencia, Faltante: f.faltante,
        })),
        sheetName: "Requisicion",
        sheetFilter: COLUMNAS,
        sheetHeader: COLUMNAS,
      }],
    }).saveExcel();
  }

  const faltantes = filas.filter((f) => f.faltante > 0).length;

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_telas || user.menu_produccion ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <i className="fas fa-clipboard-list" style={{ marginRight: 8, color: "#8b5cf6" }} />
                Requisicion de Pedidos
              </h3>
              <p className="page-subtitle">
                Lo que piden varios pedidos contra lo que hay en bodega y lo ya mandado a los maquileros
              </p>
            </div>
            {filas.length ? (
              <div>
                <Button color="danger" size="sm" onClick={PDF} style={{ marginRight: 6 }}>
                  PDF
                </Button>
                <Button color="success" size="sm" onClick={excel}>
                  Excel
                </Button>
              </div>
            ) : undefined}
          </div>

          <Row style={{ marginBottom: 16 }}>
            <Col md={6}>
              <Label>Pedidos</Label>
              <Autocomplete
                multiple
                size="small"
                options={pedidos}
                value={elegidos}
                onChange={(e, valor) => setElegidos(valor)}
                getOptionLabel={(p) =>
                  `${p.idPedido} ${p.clientes?.[0]?.nombre_comercial ?? ""} ${p.pedidoCliente ?? ""}`.trim()
                }
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Busca por numero o cliente" />}
              />
            </Col>
            <Col md={2}>
              <Label>Material</Label>
              <Input type="select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <option value="Habilitacion">Habilitacion</option>
                <option value="Telas">Tela</option>
                <option value="todos">Todo</option>
              </Input>
            </Col>
            <Col md={4} style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
              <Button color="primary" onClick={generar} disabled={cargando}>
                {cargando ? <Spinner size="sm" /> : "Generar"}
              </Button>
              {filas.length ? (
                <Button
                  size="sm"
                  color={soloFaltante ? "danger" : "secondary"}
                  onClick={() => setSoloFaltante(!soloFaltante)}
                >
                  {soloFaltante ? `Viendo solo faltantes (${faltantes})` : `Ver solo faltantes (${faltantes})`}
                </Button>
              ) : undefined}
            </Col>
          </Row>

          {filas.length ? (
            <p className="text-muted">
              Incluye la merma con la que se mandaria a maquila. "Existencia Bodega" no cuenta el material que ya
              tienen los maquileros.
            </p>
          ) : undefined}

          <Table size="sm" striped borderless responsive>
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Material</th>
                <th>Descripcion</th>
                <th>Color</th>
                <th>Unidad</th>
                <th style={{ textAlign: "right" }}>Requerido</th>
                <th style={{ textAlign: "right" }}>Ya mandado</th>
                <th style={{ textAlign: "right" }}>Por mandar</th>
                <th style={{ textAlign: "right" }}>Existencia Bodega</th>
                <th style={{ textAlign: "right" }}>Faltante</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((f) => (
                <tr key={`${f.tipoMaterial}${f.material}${f.colores}`}>
                  <td>{tipoTexto(f.tipoMaterial)}</td>
                  <td>{f.nombre}</td>
                  <td>{f.descripcion}</td>
                  <td>{f.color}</td>
                  <td>{f.unidad}</td>
                  <td style={{ textAlign: "right" }}>{n(f.requerido)}</td>
                  <td style={{ textAlign: "right" }}>{n(f.enviado)}</td>
                  <td style={{ textAlign: "right" }}>{n(f.porEnviar)}</td>
                  <td style={{ textAlign: "right" }}>{n(f.existencia)}</td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: 600,
                      color: f.faltante > 0 ? "#dc2626" : "#16a34a",
                    }}
                  >
                    {n(f.faltante)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : undefined}
    </>
  );
}

export default ReporteRequisicion;
